'use client'

import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { useAiChatUi } from '@/entities/ai-chat'

import { bitrixChat } from '@/shared/integrations/BitrixChat'
import { lockPageScroll } from '@/shared/lib/dom/lock-page-scroll'
import { useMediaQuery } from '@/shared/lib/hooks/useMediaQuery'
import { Drawer } from '@/shared/ui/drawer'

import { useBitrixOperatorTransfer } from './lib/use-bitrix-operator-transfer'
import Chat from './ui/chat'
import { OpenChatButton } from './ui/open-chat-button/open-chat-button'
import { OpenChatButtonIcon } from './ui/open-chat-button/open-chat-button-icon'
import { OpenChatButtonMin } from './ui/open-chat-button/open-chat-button-min'

import cls from './chat-widget.module.scss'

const MOBILE_CHAT_QUERY = '(max-width: 1299px)'

export const DynamicAiChatWidget = dynamic(
	() => import(`src/widgets/Ai-chat/chat-widget`).then(({ AiChatWidget }) => AiChatWidget),
	{
		ssr: false
	}
)

type AiChatWidgetProps = {
	minButtonStyle?: boolean
}

export const AiChatWidget = ({ minButtonStyle }: AiChatWidgetProps) => {
	const { isOpen, open, close, portalTarget } = useAiChatUi()
	const searchParams = useSearchParams()
	const isMobile = useMediaQuery(MOBILE_CHAT_QUERY)
	const scrollYRef = useRef(0)
	const { goToOperator } = useBitrixOperatorTransfer(close)

	useEffect(() => {
		if (searchParams.get('chat') === 'open') {
			scrollYRef.current = window.scrollY || document.documentElement.scrollTop
			open()
		}
	}, [searchParams, open])

	useEffect(() => {
		if (!(isMobile && isOpen)) return

		return lockPageScroll({ scrollY: scrollYRef.current })
	}, [isMobile, isOpen])

	const onAiChatOpen = () => {
		scrollYRef.current = window.scrollY || document.documentElement.scrollTop
		bitrixChat.close()
		open()
	}

	const renderButton = () => {
		if (portalTarget) {
			return createPortal(<OpenChatButtonIcon open={onAiChatOpen} isOpen={isOpen} />, portalTarget)
		}

		if (minButtonStyle) return <OpenChatButtonMin open={onAiChatOpen} isOpen={isOpen} />
		return <OpenChatButton open={onAiChatOpen} isOpen={isOpen} />
	}

	return (
		<div className={cls.root}>
			{renderButton()}
			{isMobile ? (
				isOpen &&
				createPortal(
					<div className={cls.mobile_fullscreen_chat}>
						<Chat goToOperator={goToOperator} onClose={close} isOpen={isOpen} />
					</div>,
					document.body
				)
			) : (
				<Drawer
					isOpen={isOpen}
					onClose={close}
					direction="right"
					className={cls.wrap}
					closeOnBackdrop={false}
					contentClassName={cls.wrap_content}>
					<Chat goToOperator={goToOperator} onClose={close} isOpen={isOpen} isDesktop={!isMobile} />
				</Drawer>
			)}
		</div>
	)
}
